import config from "@/config"
import { handleAndTrackError } from "@/lib/helper"

/**
 * Starting KYC for a FEXPRIME tenant.
 *
 * Shared by the D2C card (`components/banking/StepCard.jsx`) and the B2B card
 * (`b2b/components/OnboardingStepsCard.jsx`), which previously carried the same block
 * copy-pasted — and drifted apart once already.
 *
 * WHY THERE ARE TWO ENDPOINTS HERE
 *
 * `/ekyc/fexpex/start-kyc` is the new, CKYC-aware one. It answers a discriminated
 * `{ action }` so new variants can be added later without breaking anyone:
 *
 *   { action: "REDIRECT",          refId, sessionUrl }   → send the tenant to Digio
 *   { action: "SHOW_CKYC_CONFIRM", refId, ckyc }         → show "is this you?" first
 *
 * `/ekyc/start-kyc-fexpex` is the OLD one and is frozen forever. It is kept here as the
 * fallback for the rolling-deploy window: this bundle can reach a cpcore that has not been
 * deployed yet, where the new route simply 404s.
 *
 * FAIL OPEN, NEVER DEAD. Anything unexpected — an unknown `action`, a missing field, a
 * malformed body, a 404 — falls back to starting the eKYC, which is exactly what production
 * does today. The old code did the opposite: it treated a response without `sessionUrl` as a
 * hard failure and stopped, which is precisely why the response shape could never be
 * evolved. eKYC is mandatory, so "carry on to eKYC" is always the safe answer.
 */

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem(config.LOCAL_KEYS.SIGNUP_TOKEN)}`,
})

/**
 * fexpex wraps the CKYC search as `{ data: [record], status, message }`, while the native
 * flow uses `{ result: [record] }`. Normalise to the single record the confirm screen reads
 * so neither shape has to leak into the component.
 */
export function extractCkycRecord(ckyc) {
  if (!ckyc) return null
  const candidate = ckyc.data ?? ckyc.result ?? ckyc
  return Array.isArray(candidate) ? candidate[0] : candidate
}

/** The frozen endpoint. Used only as the fallback below. */
async function startLegacyFexprimeKyc() {
  const response = await fetch(`${config.API_URL}/ekyc/start-kyc-fexpex`, {
    method: "POST",
    headers: authHeaders(),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok || !body?.sessionUrl) {
    handleAndTrackError(
      new Error(body?.message || "Failed to start FEXPRIME KYC session."),
      {
        apiPath: "ekyc/start-kyc-fexpex",
        responseStatus: response.status,
        responseError: body,
      }
    )
    return false
  }
  window.location.href = body.sessionUrl
  return true
}

/**
 * @param navigate react-router's navigate, used for the CKYC confirm screen
 * @param partner  partner identifier, preserved on the confirm-screen URL
 * @returns true when the tenant has been sent somewhere, false when nothing could be started
 */
export async function startFexprimeKyc({ navigate, partner }) {
  let body = null
  let status = 0

  try {
    const response = await fetch(`${config.API_URL}/ekyc/fexpex/start-kyc`, {
      method: "POST",
      headers: authHeaders(),
    })
    status = response.status
    body = await response.json().catch(() => null)

    // Not deployed yet, or a server-side failure: fall back to the frozen endpoint rather
    // than showing the tenant an error for something that is purely our rollout timing.
    if (!response.ok) {
      return startLegacyFexprimeKyc()
    }
  } catch (error) {
    handleAndTrackError(error, { apiPath: "ekyc/fexpex/start-kyc", responseStatus: status })
    return startLegacyFexprimeKyc()
  }

  if (body?.action === "SHOW_CKYC_CONFIRM") {
    const record = extractCkycRecord(body.ckyc)
    // No record to render means there is nothing to confirm — go and do the eKYC instead of
    // showing an empty screen.
    if (record) {
      navigate(`/kyc${partner ? `?partner=${partner}` : ""}`, {
        state: { data: record, kycRefId: body.refId, lender: "FEXPRIME" },
      })
      return true
    }
  }

  if (body?.sessionUrl) {
    window.location.href = body.sessionUrl
    return true
  }

  // Unknown action, or an answer this bundle is too old to understand. Degrade to today's
  // behaviour rather than stranding the tenant.
  return startLegacyFexprimeKyc()
}
